import { useEffect, useMemo, useState } from 'react';
import { Filter, Search, ServerCog, SlidersHorizontal } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDate, get, qs } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';

type System = {
  id: string;
  code: string;
  name: string;
  short_name: string;
  direction_id: string;
  direction_name: string;
  direction_number: number;
  status: string;
  criticality: string;
  availability_30d: number | null;
  monitoring_enabled: number;
  responsible: string;
  problem_summary: string;
  last_update_at: string | null;
  license_status: string;
  license_expires_at: string | null;
};

const licenseNames: Record<string, string> = {
  unknown: 'Не оценено',
  licensed: 'Есть',
  subscription: 'Подписка',
  trial: 'Пробная',
  not_required: 'Не требуется',
  missing: 'Нет',
  expired: 'Истекла'
};

export function SystemsPage() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<System[]>([]);
  const [directions, setDirections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const filters = {
    q: params.get('q') || '',
    status: params.get('status') || '',
    direction: params.get('direction') || ''
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      get<System[]>('/api/systems' + qs(filters)),
      get<any[]>('/api/directions')
    ]).then(([systems, directionItems]) => {
      setItems(systems);
      setDirections(directionItems);
    }).finally(() => setLoading(false));
  }, [params.toString()]);

  const groups = useMemo(() => {
    const map = new Map<string, System[]>();
    for (const item of items) {
      if (!map.has(item.direction_name)) map.set(item.direction_name, []);
      map.get(item.direction_name)!.push(item);
    }
    return [...map.entries()];
  }, [items]);

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next);
  };

  return <>
    <section className="filter-bar">
      <div className="search-box">
        <Search size={18} />
        <input placeholder="Система, сокращение, проблема…" value={filters.q} onChange={event => update('q', event.target.value)} />
      </div>
      <label><Filter size={16} />
        <select value={filters.direction} onChange={event => update('direction', event.target.value)}>
          <option value="">Все направления</option>
          {directions.map(direction => <option key={direction.id} value={direction.id}>{direction.number}. {direction.name}</option>)}
        </select>
      </label>
      <label><SlidersHorizontal size={16} />
        <select value={filters.status} onChange={event => update('status', event.target.value)}>
          <option value="">Все статусы</option>
          <option value="not_configured">Не настроено</option>
          <option value="operational">Штатно</option>
          <option value="degraded">С отклонениями</option>
          <option value="partial_outage">Частичный отказ</option>
          <option value="outage">Недоступно</option>
          <option value="maintenance">ТО</option>
        </select>
      </label>
      <div className="result-count">Найдено: <strong>{items.length}</strong></div>
    </section>

    {loading
      ? <div className="skeleton-page" />
      : groups.length
        ? <div className="system-groups">{groups.map(([name, systems]) => <section className="panel" key={name}>
            <div className="panel-head">
              <div>
                <div className="eyebrow">Направление {systems[0].direction_number}</div>
                <h3>{name}</h3>
              </div>
              <span className="count-pill">{systems.length}</span>
            </div>
            <div className="system-table">
              <div className="system-row header">
                <span>№</span>
                <span>Система</span>
                <span>Статус</span>
                <span>Критичность</span>
                <span>Доступность</span>
                <span>Обновление</span>
                <span>Лицензия</span>
                <span>Ответственный</span>
              </div>
              {systems.map(system => <Link className="system-row" to={`/systems/${system.id}`} key={system.id}>
                <span className="system-code">{system.code}</span>
                <span className="system-name">
                  <span className="system-icon"><ServerCog size={17} /></span>
                  <span>
                    <strong>{system.name}</strong>
                    <small>{system.short_name || system.problem_summary || 'Карточка не заполнена'}</small>
                  </span>
                </span>
                <span><StatusBadge value={system.status} /></span>
                <span><span className={`criticality crit-${system.criticality}`}>{system.criticality}</span></span>
                <span className="availability">{system.availability_30d === null ? '—' : `${system.availability_30d}%`}</span>
                <span className="system-date">{formatDate(system.last_update_at, false)}</span>
                <span>
                  <span className={`license-state license-${system.license_status || 'unknown'}`}>
                    {licenseNames[system.license_status || 'unknown'] || system.license_status}
                  </span>
                  {system.license_expires_at && <small className="license-expiry">до {formatDate(system.license_expires_at, false)}</small>}
                </span>
                <span className="responsible">{system.responsible || '—'}</span>
              </Link>)}
            </div>
          </section>)}</div>
        : <div className="empty"><ServerCog /><strong>Ничего не найдено</strong><span>Измените параметры фильтра.</span></div>}
  </>;
}
